import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { useCart } from "../context/CartContext";
import CartItems from "../pages/canvas/CartItems";
import { useSiteConfig } from "../context/SiteConfigContext";

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
  >
    <path d="M2 2l12 12M14 2L2 14" />
  </svg>
);

const ArrowRight = () => (
  <svg
    className="cart-arrow"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M1 6h10M7 1l5 5-5 5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BagIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
  >
    <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

const FREE_SHIPPING_THRESHOLD = 10000;

// ─── Main CartOffCanvas ───────────────────────────────────────────────────────
export default function CartOffCanvas({ isOpen, onClose }) {
  // ── Real cart data from context ──────────────────────────────────────────
  const { cartItems, cartSummary } = useCart();
  const { config } = useSiteConfig();

  const backdropRef = useRef(null);
  const sidebarRef = useRef(null);
  const itemsRef = useRef(null);
  const emptyRef = useRef(null);
  const tlRef = useRef(null);

  const isEmpty = !cartItems || cartItems.length === 0;

  // ── Derived values from cartSummary (API provided) ───────────────────────
  // cartSummary.subtotal, .discount, .shipping, .total from your CartContext
  const subtotal = Number(cartSummary?.subtotal) || 0;
  const discount = Number(cartSummary?.discount) || 0;
  const total = Number(cartSummary?.total) || subtotal;
  const freeShipping = Number(cartSummary?.shipping) === 0;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const shippingPct = Math.min(
    100,
    Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100),
  );

  // Total qty count across all items
  const totalQty = cartItems.reduce(
    (s, it) => s + (Number(it.quantity) || 1),
    0,
  );

  const handleClose = useCallback(() => {
    if (sidebarRef.current && backdropRef.current) {
      const closeTl = gsap.timeline({ onComplete: onClose });
      closeTl
        .to(
          sidebarRef.current,
          { x: "100%", duration: 0.45, ease: "power3.in" },
          0,
        )
        .to(
          backdropRef.current,
          { opacity: 0, duration: 0.35, ease: "power2.in" },
          0.05,
        );
    } else {
      onClose();
    }
  }, [onClose]);

  // ── GSAP open animation ──────────────────────────────────────────────────
  useEffect(() => {
    if (!backdropRef.current || !sidebarRef.current) return;

    if (isOpen) {
      document.body.style.overflow = "hidden";
      backdropRef.current.classList.add("is-visible");

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tlRef.current = tl;

      tl.fromTo(
        backdropRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.4 },
        0,
      ).fromTo(
        sidebarRef.current,
        { x: "100%" },
        { x: "0%", duration: 0.52 },
        0.05,
      );

      if (!isEmpty && itemsRef.current) {
        const cards = itemsRef.current.querySelectorAll(".cart-item");
        tl.to(
          cards,
          {
            opacity: 1,
            x: 0,
            duration: 0.45,
            stagger: 0.08,
            ease: "power3.out",
          },
          0.35,
        );
      }

      if (isEmpty && emptyRef.current) {
        tl.to(emptyRef.current, { opacity: 1, duration: 0.5 }, 0.4);
      }
    } else {
      document.body.style.overflow = "";
      backdropRef.current.classList.remove("is-visible");
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, isEmpty]);

  // Re-animate items when cart updates while open
  useEffect(() => {
    if (!isOpen || isEmpty || !itemsRef.current) return;
    const cards = itemsRef.current.querySelectorAll(".cart-item");
    gsap.to(cards, {
      opacity: 1,
      x: 0,
      duration: 0.4,
      stagger: 0.07,
      ease: "power3.out",
      overwrite: true,
    });
  }, [cartItems, isOpen, isEmpty]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, handleClose]);

  return (
    <>
      {/* ── Backdrop ── */}
      <div
        ref={backdropRef}
        className="cart-backdrop"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* ── Sidebar ── */}
      <div
        ref={sidebarRef}
        className="cart-sidebar"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        {/* Header */}
        <div className="cart-header">
          <div className="cart-header-left">
            <span className="cart-header-eyebrow">
              <Link to="/" className="brand-logo">
                <img src={config?.logo} alt="logo" />
              </Link>
            </span>
            <h2 className="cart-title">
              Your Cart
              {!isEmpty && (
                <span
                  className="cart-count-badge"
                  aria-label={`${totalQty} items`}
                >
                  {totalQty}
                </span>
              )}
            </h2>
          </div>
          <button
            className="cart-close-btn"
            onClick={handleClose}
            aria-label="Close cart"
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        {isEmpty ? (
          /* Empty State */
          <div className="cart-body">
            <div className="cart-empty" ref={emptyRef}>
              <div className="cart-empty-icon" aria-hidden="true">
                <BagIcon />
              </div>
              <h3 className="cart-empty-heading">Your cart is empty</h3>
              <p className="cart-empty-text">
                Discover our curated collection of handcrafted ethnic pieces —
                something beautiful is waiting for you.
              </p>
              <Link to="/shop" className="cart-empty-btn" onClick={handleClose}>
                <span>Start Shopping</span>
                <ArrowRight />
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Items */}
            <div className="cart-body" ref={itemsRef}>
              {cartItems.map((item, index) => (
                <CartItems key={item._id || item.id || index} item={item} />
              ))}
            </div>

            {/* Summary */}
            <div className="cart-summary">
              {/* Savings banner — only if discount exists */}
              {discount > 0 && (
                <div className="cart-savings-banner" aria-live="polite">
                  <span aria-hidden="true">✦</span>
                  <span className="cart-savings-text">
                    You're saving ₹{Number(discount).toLocaleString("en-IN")}
                  </span>
                </div>
              )}

              {/* Free shipping progress */}
              {/* <div className="cart-shipping-progress">
                <div className="cart-shipping-progress-label">
                  <span>
                    {freeShipping
                      ? "Free"
                      : `₹${Number(cartSummary?.shipping).toLocaleString("en-IN")}`}
                  </span>
                  <span>{shippingPct}%</span>
                </div>
                <div className="cart-shipping-track" aria-hidden="true">
                  <div
                    className="cart-shipping-fill"
                    style={{ width: `${shippingPct}%` }}
                  />
                </div>
              </div> */}

              {/* Subtotal */}
              <div className="cart-summary-row">
                <span className="cart-summary-label">Subtotal</span>
                <span className="cart-summary-value">
                  ₹{subtotal.toLocaleString("en-IN")}
                </span>
              </div>

              {/* Discount row — only if applicable */}
              {discount > 0 && (
                <div className="cart-summary-row">
                  <span className="cart-summary-label">Discount</span>
                  <span
                    className="cart-summary-value"
                    style={{ color: "var(--color-accent-main)" }}
                  >
                    - ₹{Number(discount).toLocaleString("en-IN")}
                  </span>
                </div>
              )}

              {/* Shipping */}
              <div className="cart-summary-row">
                <span className="cart-summary-label">Shipping</span>
                <span
                  className={
                    freeShipping
                      ? "cart-summary-value--free"
                      : "cart-summary-value"
                  }
                >
                  {freeShipping
                    ? "Free"
                    : `₹${Number(cartSummary?.shipping).toLocaleString("en-IN")}`}
                </span>
              </div>

              {/* Total */}
              <div className="cart-total-row">
                <span className="cart-total-label">Total</span>
                <span className="cart-total-value">
                  ₹{total.toLocaleString("en-IN")}
                </span>
              </div>

              <span className="cart-tax-note">
                Taxes and duties calculated at checkout
              </span>

              <div className="cart-actions">
                <Link
                  to="/cart"
                  className="cart-btn-checkout"
                  onClick={handleClose}
                >
                  <span>Proceed to Cart</span>
                  <ArrowRight />
                </Link>
                {/* <button
                  type="button"
                  className="cart-btn-continue"
                  onClick={handleClose}
                >
                  Continue Shopping
                </button> */}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
